import { ComponentChild } from 'preact';
import { useLocation } from 'preact-iso';
import {
  useEffect,
  useLayoutEffect,
  useMemo,
  useReducer,
  useRef,
  useState,
} from 'preact/hooks';
import useSWR from 'swr';
import useSWRImmutable from 'swr/immutable';
import waitForTimers from 'wait-for-timers';
import { ButtonBar } from '../../components/button-bar';
import { SwipeAction } from '../../components/episode-item';
import { Icon } from '../../components/icon';
import { LoadingBar } from '../../components/loading-bar';
import TopNav from '../../components/top-nav';
import { VirtualizedEpisodesList } from '../../components/virtualized-episode-list';
import { descriptionActions } from '../../global-state/episode-descriptions';
import { usePodcastsStore } from '../../global-state/podcasts';
import { updatePodcastPageState, useSessionStore } from '../../global-state/session';
import {
  bumpLastViewedEpisodeDate,
  followPodcast,
  setPlayed,
  setUnPlayed,
  unfollowPodcast,
  userInteractedWithEpisodeIds,
  useUserDataStore,
} from '../../global-state/user-data';
import { Episode, Podcast, PodcastId } from '../../models/podcasts';
import { fetchEpisodes, fetchPodcast } from '../../utils/api';
import { checkIfPlayed } from '../../utils/check-if-played';
import getThumbUrl from '../../utils/get-thumb';
import { classNames } from '../../utils/helpers';
import { usePageTitle } from '../../utils/page-title';
import {
  filterActions,
  isFilterApplied,
  useFilteredAndSortedEpisodes,
  useFilterStore,
  useSeasons,
} from './filter-store';
import style from './style.module.scss';

export default function PodcastRoute(params: { id: string }) {
  const id = params.id as PodcastId;
  const podcast: Podcast | undefined = usePodcastsStore((store) => store.podcasts[id]);
  usePageTitle(podcast?.name);

  const followedPodcastIds = useUserDataStore((store) => store.followedPodcastIds);
  const previousRoute = useSessionStore((s) => s.previousRoute);
  const { route } = useLocation();

  // Ensure we always have the latest version of this podcast
  const { error, isLoading } = useSWR(['podcast', id], () => fetchPodcast(id));

  // Ensure we always check for the latest episodes
  const { data: latestEpisodes, isLoading: isLoadingLatest } = useSWR(
    ['podcast', id, 'latest-episodes'],
    () => fetchEpisodes(id, 'latest')
  );

  // Ensure we have all episodes (only needs to be done once)
  const { isLoading: isLoadingAll } = useSWRImmutable(
    ['podcast', id, 'all-episodes'],
    () => fetchEpisodes(id, 'all')
  );

  const latestEpisodePubDate = podcast?.episodes[0]?.pubDate.toISOString();
  useEffect(() => {
    if (latestEpisodePubDate)
      // Mark this podcast's episodes as having been recently viewed by the user
      // after they've been on the page for a moment
      return waitForTimers([2000], () =>
        bumpLastViewedEpisodeDate(podcast!.episodes[0])
      );
  }, [latestEpisodePubDate]);

  // Grab latest descriptions from API after checking for latest episodes
  useEffect(() => {
    if (latestEpisodes) descriptionActions.fetchPodcastDescriptionsFromApi(id);
  }, [latestEpisodes?.episodes[0]?.guidHash]);

  let { description, optOutReason, language } = podcast || {};
  if (!description) {
    description = error
      ? `Failed to load description: ${error}`
      : isLoading
        ? 'Loading description...'
        : 'Missing description';
  }

  const showNewPodcastMessage =
    !isLoadingLatest &&
    !isLoadingAll &&
    !optOutReason &&
    podcast?.episodes?.length &&
    podcast?.episodes.every((ep) => !ep.isSaved && ep.status === 'pending');

  // Filter out omitted episodes that the user hasn't interacted with
  const visibleEpisodes = podcast?.episodes.filter(
    (ep) => !ep.omit || userInteractedWithEpisodeIds.has(ep.id)
  );

  return (
    <>
      <TopNav />
      <div class="top-section">
        {podcast ? (
          <>
            <div class={style.podcastMetadata}>
              <img src={getThumbUrl(podcast, 200)} alt="" width="200" height="200" />
              {previousRoute && (
                <button
                  class={`button ${style.backButton}`}
                  onClick={() => {
                    // Workaround for Android where for some reason, when first opening the app and navigating to
                    // episode or podcast, Back button doesn't work even though history.length === 2...
                    if (previousRoute === '/now') route('/now');
                    else history.back();
                  }}
                >
                  « Back
                </button>
              )}
              <h1>{podcast.name}</h1>
              <div>
                {podcast.episodeCount} episode{podcast.episodeCount === 1 ? '' : 's'} by{' '}
                {podcast.artistName}
              </div>
              <div class="mb-1"></div>
              <div class="mb-1">
                {followedPodcastIds.includes(id) ? (
                  <button
                    class="button button--favorite-with-text"
                    onClick={() => unfollowPodcast(podcast)}
                  >
                    <span>Following</span>
                    <Icon class="text-green-600" sprite="checkmark-circle" />
                  </button>
                ) : (
                  <button
                    class="button button--favorite-with-text"
                    onClick={() => followPodcast(podcast)}
                  >
                    <span>Follow</span>
                    <Icon sprite="plus-circle" />
                  </button>
                )}
              </div>
              {optOutReason && (
                <aside class="my-2 rounded border border-amber-400 bg-yellow-50 p-2 text-sm">
                  <span class="font-normal">
                    Automatic ad-skipping is disabled for this podcast :(
                  </span>
                  <br />
                  {optOutReason}
                </aside>
              )}
              <div
                class={style.description}
                dangerouslySetInnerHTML={{ __html: description }}
              />
              {Boolean(language && !language.toLowerCase().startsWith('en')) && (
                <aside class="my-3 mt-2 rounded border border-amber-400 bg-yellow-50 p-2 text-sm">
                  Ad detection may be less accurate for non-English podcasts
                </aside>
              )}
              {showNewPodcastMessage && (
                <aside class="my-3 rounded border border-green-400 bg-green-50 p-2 text-sm">
                  We're preparing this podcast for first-time playback! Ad detection
                  takes a few minutes per episode.
                  <br />
                  <br />
                  Want to play a specific episode? Click it so we know to detect ads in
                  it first.
                </aside>
              )}
              <div class="relative mb-1 mt-2 h-0.5 bg-slate-200">
                <LoadingBar
                  isLoading={isLoading || isLoadingLatest || isLoadingAll}
                  className="absolute left-0"
                />
              </div>
              {visibleEpisodes?.length ? (
                <FilteredEpisodes podcastId={id} episodes={visibleEpisodes} />
              ) : (
                <div className="my-10 text-center text-sm text-gray-600">
                  {isLoadingLatest || isLoadingAll
                    ? 'Loading episodes...'
                    : 'No episodes found.'}
                </div>
              )}
            </div>
          </>
        ) : isLoading ? (
          <div class="flex h-32 items-center justify-center">Loading podcast...</div>
        ) : error ? (
          <div>
            <p>{`Unable to fetch podcast: ${error}`}</p>
            <p>
              <a href="/search">Search Podcasts</a>
            </p>
          </div>
        ) : (
          ''
        )}
      </div>
    </>
  );
}

const playedFilterButtons = [
  {
    id: 'all',
    children: 'All',
  },
  {
    id: 'unplayed',
    children: 'Unplayed',
  },
  {
    id: 'played',
    children: 'Played',
  },
] as const;

function FilteredEpisodes({
  podcastId,
  episodes: allEpisodes,
}: {
  podcastId: PodcastId;
  episodes: Episode[];
}) {
  const filters = useFilterStore();
  const seasons = useSeasons(allEpisodes);
  const episodes = useFilteredAndSortedEpisodes(allEpisodes, filters);

  const [episodesAndOtherUi, stickyHeadingIndices] = useMemo(() => {
    const headingIndices: number[] = [];
    if (seasons && !filters.search) {
      const episodesWithSeasonHeadings: (Episode | ComponentChild)[] = [];
      const startedSeasons = new Set<number>();
      for (const episode of episodes) {
        if (episode.season && !startedSeasons.has(episode.season)) {
          startedSeasons.add(episode.season);
          headingIndices.push(episodesWithSeasonHeadings.length);
          episodesWithSeasonHeadings.push(
            <h2 class="mt-2 border-b text-xl">Season {episode.season}</h2>
          );
        }
        episodesWithSeasonHeadings.push(episode);
      }
      return [episodesWithSeasonHeadings, headingIndices];
    }
    return [episodes, headingIndices];
  }, [episodes, seasons, filters.season, filters.search]);

  const searchInputRef = useRef<HTMLInputElement>(null);
  const [isSearchFocused, setIsSearchFocused] = useState(false);

  // Set initial filters & save to memory when they're updated
  useLayoutEffect(() => {
    const initialFilters = useSessionStore.getState().podcastPageState[podcastId];
    if (initialFilters) {
      filterActions.setAll(initialFilters);
      if (initialFilters.search && searchInputRef.current) {
        searchInputRef.current.focus(); // Make it very clear that a search is active
      }
    }
  }, []);
  useEffect(() => updatePodcastPageState(podcastId, filters), [filters, podcastId]);

  const forceUpdate = useReducer(() => ({}), {})[1] as () => void;

  return (
    <>
      <div class="scrollbar-none overflow-x-auto">
        <nav className="flex h-12 items-center gap-2 [&_*]:shrink-0">
          <div class="flex h-8 rounded-lg border border-gray-200" role="tablist">
            <ButtonBar
              buttons={playedFilterButtons}
              selectedId={filters.played}
              onSelect={(tab) => filterActions.setPlayed(tab)}
              buttonClassName={(id) =>
                classNames(
                  'rounded-lg px-1.5 min-w-10 h-[30px]',
                  id === 'all'
                    ? '[&_span]:rounded-r-none [&_span]:rounded-l-lg'
                    : id === 'played'
                      ? '[&_span]:rounded-l-none [&_span]:rounded-r-lg'
                      : ''
                )
              }
            />
          </div>
          <label
            class={classNames(
              'relative flex h-8 w-[88px] items-center justify-center text-[14px] font-medium transition-all',
              'focus-within:w-40',
              filters.search && 'w-40'
            )}
          >
            <span class="sr-only">Search</span>
            <input
              type="text"
              defaultValue={filters.search}
              onInput={(e) => filterActions.setSearch(e.currentTarget.value)}
              class={classNames(
                'peer h-8 w-full px-2 text-[14px] font-medium placeholder:text-gray-700',
                'rounded-lg border border-gray-200',
                filters.search && 'border-indigo-500'
              )}
              placeholder={isSearchFocused ? 'Type to filter...' : 'Search'}
              ref={searchInputRef}
              onFocus={() => setIsSearchFocused(true)}
              onBlur={() => setIsSearchFocused(false)}
            />
            {filters.search ? (
              <button
                type="button"
                aria-label="Clear search"
                title="Clear search"
                class="absolute right-1 flex h-6 w-6 cursor-pointer items-center justify-center"
                onClick={() => {
                  filterActions.setSearch('');
                  if (searchInputRef.current) searchInputRef.current.value = '';
                }}
              >
                <Icon sprite="close" class="h-3 w-3 text-gray-600" />
              </button>
            ) : (
              <Icon
                class="absolute right-2 h-5 w-5 opacity-100 transition-opacity peer-focus:opacity-0"
                sprite="search"
              />
            )}
          </label>
          {seasons && (
            <label>
              <span class="sr-only">Season</span>
              <select
                class={classNames(
                  'flex h-8 w-32 items-center gap-1 rounded-lg border border-gray-200 px-2 py-0 text-[14px] font-medium',
                  filters.season !== 'all' && 'border-indigo-500'
                )}
                value={filters.season.toString()}
                onChange={(e) => {
                  // @ts-expect-error
                  filterActions.setSeason(parseInt(e.target.value) || 'all');
                }}
              >
                <option value="all">All Seasons</option>
                {seasons.map((season) => (
                  <option value={season}>Season {season}</option>
                ))}
              </select>
            </label>
          )}
          <button
            class={classNames(
              'flex h-8 items-center gap-1 rounded-lg border border-gray-200 px-2 text-[14px] font-medium',
              filters.search && 'cursor-not-allowed text-gray-600 opacity-80'
            )}
            onClick={() =>
              filterActions.setSort(filters.sort === 'newest' ? 'oldest' : 'newest')
            }
            disabled={Boolean(filters.search)}
          >
            <span>{filters.sort === 'newest' ? 'Newest' : 'Oldest'}</span>
            <Icon
              class={classNames(
                'h-5 w-5 transition-transform duration-100',
                filters.sort === 'oldest' ? 'scale-y-[-1] transform' : ''
              )}
              sprite="sort"
            />
          </button>
        </nav>
      </div>
      {!episodes.length && isFilterApplied(filters) && (
        <div class="my-10 flex flex-col items-center gap-2 text-center text-sm text-gray-600">
          No episodes found w/ this filter.
          <button
            class="flex h-8 items-center rounded-lg border border-gray-200 px-2 text-[14px] font-medium"
            onClick={() => {
              filterActions.reset();
              if (searchInputRef.current) searchInputRef.current.value = '';
            }}
          >
            Clear filters
          </button>
        </div>
      )}
      <VirtualizedEpisodesList
        episodes={episodesAndOtherUi}
        swipeAction={getSwipeAction}
        stickyHeadingIndices={stickyHeadingIndices}
      />
    </>
  );

  function getSwipeAction(episode: Episode): SwipeAction | undefined {
    if (filters.played === 'unplayed') {
      return {
        onSwipe: (animationDuration: number) => {
          waitForTimers([animationDuration, 'ric', 'raf'], () => {
            setPlayed(episode);
            forceUpdate();
          });
          return 'dismiss';
        },
        text: 'Set Played',
        color: 'gray',
      };
    }
    if (filters.played === 'played') {
      return {
        onSwipe: (animationDuration: number) => {
          waitForTimers([animationDuration, 'ric', 'raf'], () => {
            setUnPlayed(episode);
            forceUpdate();
          });
          return 'dismiss';
        },
        text: 'Set Unplayed',
        color: 'gray',
      };
    }
    const isPlayed = checkIfPlayed(episode);
    return {
      onSwipe: (animationDuration: number) => {
        waitForTimers([animationDuration, 'ric', 'raf'], () => {
          isPlayed ? setUnPlayed(episode) : setPlayed(episode);
          forceUpdate();
        });
      },
      text: isPlayed ? 'Set Unplayed' : 'Set Played',
      color: 'gray',
    };
  }
}
